import { onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * Arrastar-para-rolar num container de scroll horizontal.
 *
 * O `overflow-x` nativo só entrega barra de rolagem, roda e trackpad. Clicar e
 * empurrar o conteúdo com o mouse — o gesto que todo carrossel de vitrine
 * promete — não existe no browser: precisa de JS escrevendo `scrollLeft`.
 *
 * Também mede se há overflow de verdade (`canScroll`), porque a cromagem que
 * anuncia o arraste (mãozinha, esfumaçado na borda, sombra de coluna travada)
 * mente quando o conteúdo cabe inteiro.
 */
export interface UseDragScrollOptions {
  /**
   * Quantos px o ponteiro precisa andar para o gesto virar arraste em vez de
   * clique. Abaixo disso é tremida de mão em cima de um item clicável, e
   * engolir o clique aí deixaria o item sem responder.
   */
  threshold?: number;
}

export function useDragScroll(options: UseDragScrollOptions = {}) {
  const threshold = options.threshold ?? 6;

  /** Prender no `ref="..."` do container de scroll. */
  const element = ref<HTMLElement | null>(null);

  const isDragging = ref(false);

  /** O conteúdo é mais largo que o container — ver o porquê no topo. */
  const canScroll = ref(false);

  /**
   * Largura visível do container, remedida a cada resize. Serve de gatilho
   * reativo para quem precisa recalcular medida de layout junto: `canScroll`
   * sozinho não presta para isso porque vira `true` uma vez e fica.
   */
  const viewportWidth = ref(0);

  /** Posição do ponteiro e do scroll no instante do clique. */
  let startX = 0;
  let startScroll = 0;

  /**
   * O último gesto passou do `threshold`. Fica de pé durante o `click` que o
   * browser dispara depois do `pointerup`, que é onde o consumidor lê isso para
   * não tratar o fim de um arraste como seleção de item.
   */
  let dragged = false;

  /**
   * Se já pegamos a captura do ponteiro. Ela NÃO é pedida no `pointerdown`: com
   * captura ativa o browser retargeta o `click` para o elemento capturador, e aí
   * item clicável dentro do container (cartão, link) nunca recebe o próprio
   * clique. Só pedimos depois de passar o `threshold` — quando o gesto já é
   * arraste e engolir o clique é justamente o que queremos.
   */
  let captured = false;

  const measureOverflow = () => {
    const el = element.value;

    if (!el) return;

    // 1px de folga: arredondamento de layout deixa o scrollWidth um subpixel
    // acima do clientWidth mesmo quando cabe.
    canScroll.value = el.scrollWidth - el.clientWidth > 1;
    viewportWidth.value = el.clientWidth;
  };

  const onPointerDown = (event: PointerEvent) => {
    // Só botão principal: com o do meio o browser tem o próprio auto-scroll e
    // com o direito abriria o menu de contexto no meio do arraste.
    if (event.button !== 0 || !element.value) return;

    // No toque o browser já faz o pan nativo; mexer no scrollLeft por cima
    // dobraria a velocidade do arraste.
    if (event.pointerType === 'touch') return;

    dragged = false;
    captured = false;
    isDragging.value = true;
    startX = event.clientX;
    startScroll = element.value.scrollLeft;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!isDragging.value || !element.value) return;

    const delta = event.clientX - startX;

    if (!dragged && Math.abs(delta) > threshold) {
      dragged = true;

      // Agora sim a captura: daqui pra frente o arraste continua mesmo saindo do
      // container, sem listener no document — e o clique retargetado já não
      // atrapalha, porque este gesto não é mais clique.
      element.value.setPointerCapture(event.pointerId);
      captured = true;
    }

    // Enquanto não passou do threshold ainda rolamos: o gesto curto move o
    // conteúdo um fio e volta, sem travar até o 7º pixel.
    // O conteúdo segue o dedo: arrastar pra esquerda avança o scroll.
    element.value.scrollLeft = startScroll - delta;
  };

  const endDrag = (event: PointerEvent) => {
    if (!isDragging.value) return;

    isDragging.value = false;

    if (captured) element.value?.releasePointerCapture(event.pointerId);

    captured = false;
  };

  /**
   * `true` se o gesto que acabou foi arraste. Chamar no handler de clique dos
   * itens: `if (wasDragged()) return`.
   */
  const wasDragged = () => dragged;

  /** Prender no container com `v-on="dragHandlers"`. */
  const dragHandlers = {
    pointercancel: endDrag,
    pointerdown: onPointerDown,
    pointermove: onPointerMove,
    pointerup: endDrag,
  };

  let observer: ResizeObserver | null = null;

  onMounted(() => {
    measureOverflow();

    if (typeof ResizeObserver === 'undefined' || !element.value) return;

    // Observa o container: o gatilho é a largura disponível mudando (resize da
    // janela, sidebar abrindo), não o conteúdo.
    observer = new ResizeObserver(measureOverflow);
    observer.observe(element.value);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  return {
    canScroll,
    dragHandlers,
    element,
    isDragging,
    measureOverflow,
    viewportWidth,
    wasDragged,
  };
}
